import { Button, Flex } from '@chakra-ui/core'
import React, { memo, useCallback, useMemo } from 'react'
import { ReactComponent as ArrowsIcon } from 'assets/arrows-down-up.svg'
import { useDispatch, useSelector } from 'react-redux'
import { handleGetPosts, setConsolidationFilter, setIsShowAllMyPosts, setPosts } from 'redux/actions/feed'
import { RootState } from 'constants/interfaces'
import { getPostsIdsFromLocalStorage } from 'components/Post/utils'
import { BiSearch } from 'react-icons/bi'
import { GroupedSelect } from 'components/CommonComponents/GroupedSelect'
import { ItemGroup } from 'components/CommonComponents/GroupedSelect/types'
import { Tag, Users, ListFilter } from 'lucide-react'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'

interface PostFiltersProps {
  user_groups: string[] | null
  consolidationFilterOptions: Record<string, string[]>
  isDisabled: boolean
  uid: string

  openSearchModalHandler: () => void
  isShowAllMyPosts: boolean
}

export const PostFilters = memo(
  ({
    user_groups,
    consolidationFilterOptions,
    isDisabled,
    uid,
    isShowAllMyPosts,
    openSearchModalHandler,
  }: PostFiltersProps) => {
    const dispatch = useDispatch()
    const { theme } = useSelector((state: RootState) => state.general)
    const { tags: configTags } = useSelector((state: RootState) => state.config.config)
    const { groups, tags: permittedTags } = useSelector((state: RootState) => state.config.userWritePermittedContext)

    const tags = useMemo(() => {
      return configTags.filter((tag) => permittedTags.includes(tag.sk))
    }, [configTags, permittedTags])

    const onFilterSubmit = useCallback(() => {
      dispatch(setIsShowAllMyPosts(!isShowAllMyPosts))
    }, [dispatch, isShowAllMyPosts])

    const filterConsolidationOptions: ItemGroup[] = useMemo(() => {
      const groupItems =
        user_groups && user_groups.length
          ? [
              {
                title: 'groups',
                icon: <Users />,
                items: user_groups.map((id) => ({
                  id,
                  name: groups?.[id]?.name,
                })),
              },
            ]
          : []

      const tagItems = tags.length
        ? [
            {
              title: 'tags',
              icon: <Tag />,
              items: tags.map((tag) => ({
                id: tag.sk,
                name: tag.name,
              })),
            },
          ]
        : []

      const allItem = {
        title: '',
        items: [{ id: 'all', name: 'context_post_consolidation_switch' }],
      }

      return [allItem, ...groupItems, ...tagItems]
    }, [groups, tags, user_groups])

    const getFilteredPosts = useCallback(
      (ids: string[], selectedByGroup?: Record<string, string[]>) => {
        const currentEpochTimeUTC = Math.floor(new Date().getTime() / 1000).toString()
        const pinedPostsObject = getPostsIdsFromLocalStorage()
        const hasAll = ids.includes('all')
        const handleGetPostsParams = {
          ts: currentEpochTimeUTC,
          is_init: true,
          isRefresh: true,
          ...(pinedPostsObject[uid!] ? { postIds: pinedPostsObject[uid!] } : {}),
          ...(hasAll
            ? {}
            : {
                ...(selectedByGroup?.groups ? { groups: selectedByGroup.groups } : {}),
                ...(selectedByGroup?.tags ? { tags: selectedByGroup.tags } : {}),
              }),
        }
        dispatch(handleGetPosts(handleGetPostsParams))
      },
      [dispatch, uid]
    )

    const onFilterConsolidationSubmit = useCallback(
      (selectedByGroup: Record<string, string[]>) => {
        const allSelectedIds = Object.values(selectedByGroup).flat()
        const prevIds = Object.values(consolidationFilterOptions).flat()
        if (allSelectedIds.length === prevIds.length && allSelectedIds.every((id, idx) => id === prevIds[idx])) {
          return
        }
        dispatch(setPosts({ posts: [], isRefresh: true }))
        dispatch(setConsolidationFilter(selectedByGroup))

        getFilteredPosts(allSelectedIds, selectedByGroup)
      },
      [consolidationFilterOptions, dispatch, getFilteredPosts]
    )

    // Custom trigger button for GroupedSelect
    const customTrigger = (
      <NoFocusButton
        isDisabled={isDisabled}
        color="white"
        bg={theme?.elementsColor}
        fontSize="14px"
        borderRadius="20px"
        minW="unset"
        w="40px"
        px="0"
        height="40px"
        _hover={{ opacity: 0.8 }}
        _active={{ opacity: 0.6 }}
      >
        <ListFilter size={20} />
      </NoFocusButton>
    )

    return (
      <Flex alignItems="center" style={{ columnGap: '10px' }}>
        <Button
          isDisabled={isDisabled}
          dir="ltr"
          bg="white"
          fontSize="14px"
          borderRadius="20px"
          minW="unset"
          w="40px"
          px="0"
          height="40px"
          onClick={openSearchModalHandler}
          _focus={{ boxShadow: 'none' }}
          _hover={{ bg: theme?.backgroundLight }}
          _active={{ bg: theme?.backgroundLight }}
        >
          <BiSearch height="20px" />
        </Button>
        <Button
          isDisabled={isDisabled}
          dir="ltr"
          bg={!isShowAllMyPosts ? theme?.backgroundMedium : 'white'}
          fontSize="14px"
          borderRadius="20px"
          minW="unset"
          w="40px"
          px="0"
          height="40px"
          onClick={onFilterSubmit}
          _focus={{ boxShadow: 'none' }}
          _hover={{ bg: theme?.backgroundLight }}
          _active={{ bg: theme?.backgroundLight }}
        >
          <ArrowsIcon height="20px" />
        </Button>

        <GroupedSelect
          data={filterConsolidationOptions}
          selectedIds={consolidationFilterOptions}
          onSubmit={onFilterConsolidationSubmit}
          allToggleItemId="all"
          isShowSelectAllButton={false}
          customTrigger={customTrigger}
        />
      </Flex>
    )
  }
)
